
import Vue from "vue";
import dayjs from "dayjs";
import { required } from "vuelidate/lib/validators";
import { MAX_BULK_FAULT_EVENTS } from "@/features/model/constants/faultEventAlarm";

export default Vue.extend({
  name: "FaultEventAlarmBulkAction",
  props: {
    items: { type: Array, default: () => [] },
    actionType: { type: String, default: "ack" }, // "ack" | "done" | "reject"
  },
  validations: {
    inputEmails: { required },
    inputComment: { required },
    validationGroup: ["inputEmails", "inputComment"],
  },
  computed: {
    model() {
      return this.$store.getters.model;
    },
    user() {
      return this.$store.getters.user;
    },
  },
  data() {
    return {
      dialog: false,
      inputEmails: [] as string[],
      inputComment: null as string | null,
      isSaving: false,
    };
  },
  methods: {
    open() {
      this.dialog = true;
      this.inputEmails = [];
      this.inputComment = null;
      this.$v.$reset();
    },
    onCancel() {
      this.dialog = false;
      this.inputEmails = [];
      this.inputComment = null;
      this.$v.$reset();
    },
    async onSave() {
      this.$v.$touch();
      if (this.$v.validationGroup.$invalid) return;

      if (this.items.length > MAX_BULK_FAULT_EVENTS) {
        (this as any).$store.dispatch("openAlertMessage", {
          open: true,
          message: `Maximum ${MAX_BULK_FAULT_EVENTS} events per bulk action.`,
          type: "error",
        });
        return;
      }

      const result = await (this as any).$swal({
        title: "Confirm",
        html: `Would you like to <strong>${
          this.actionType === "ack" ? "Acknowledge" : this.actionType === "done" ? "Approve" : "Reject"
        }</strong> ${this.items.length} event${this.items.length !== 1 ? "s" : ""}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        confirmButtonColor: "#db3d32",
        reverseButtons: true,
        allowOutsideClick: () => false,
      });

      if (!result.isConfirmed) return;

      const body = {
        comment_info: {
          fault_event_ids: (this.items as any[]).map((i) => i.id),
          email_to: this.inputEmails,
          comment: this.inputComment,
        },
        data: (this.items as any[]).map((item) => ({
          overAllStatus: item.alarmType === 2 ? "Alarm" : "Warning",
          date: dayjs(item.eventDate).format("DD-MM-YYYY"),
          time: dayjs(item.eventDate).format("HH:mm"),
          sensorId: item.sensorId,
          modelName: (this as any).model.modelName,
          alarmMessage: item.alarmMessage,
          cause: item.cause,
          recommendation: item.recommendation,
          alarmValue: item.alarmValue,
          warningLimit: item.warningLimit,
          alarmLimit: item.alarmLimit,
        })),
      };

      this.isSaving = true;
      try {
        if (this.actionType === "ack") {
          await (this as any).$dep.modelUseCase.postFaultEventBulkAcknowledge(
            (this as any).model.equipmentId,
            (this as any).user.email,
            body
          );
        } else if (this.actionType === "done") {
          await (this as any).$dep.modelUseCase.postFaultEventBulkDone(
            (this as any).model.equipmentId,
            (this as any).user.email,
            body
          );
        } else {
          await (this as any).$dep.modelUseCase.postFaultEventBulkReject(
            (this as any).model.equipmentId,
            (this as any).user.email,
            body
          );
        }
        (this as any).$store.dispatch("openAlertMessage", {
          open: true,
          message: `${this.actionType === "ack" ? "Acknowledge" : this.actionType === "done" ? "Approve" : "Reject"} ${
            this.items.length
          } event${this.items.length !== 1 ? "s" : ""} successfully completed.`,
          type: "success",
        });
        this.onCancel();
        this.$emit("reload");
      } catch (error: any) {
        console.error(error);
        this.onCancel();
        (this as any).$store.dispatch("openAlertMessage", {
          open: true,
          message: error.response?.data?.detail || "There was an error sending your data!",
          type: "error",
        });
      } finally {
        this.isSaving = false;
      }
    },
    formatDate(date: any, format = "DD MMM YYYY, HH:mm") {
      return dayjs(date).format(format);
    },
  },
});
